import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType, SVGProps } from "react";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";

interface WelcomeScreenProps {
  businessName: string;
  onContinue: () => void;
}

// What the next two screens actually ask for. Saying it up front means an owner can go and
// find the one answer they do not have to hand before they start, rather than getting
// stuck halfway.
const ONBOARDING_ASKS: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  detail: string;
}[] = [
  {
    icon: BuildingOffice2Icon,
    title: "Your company details",
    detail:
      "Your company name and the time zone you run payroll in. We use the time zone to line your pay periods up correctly.",
  },
  {
    icon: CalendarDaysIcon,
    title: "How you pay your team",
    detail:
      "How often you pay, and which day a pay period starts. If you pay every other week, we will also ask for a date that one of your pay periods began.",
  },
  {
    icon: DocumentTextIcon,
    title: "Your payroll documents",
    detail:
      "The final numbers from your most recent pay run, and every report and calculation you used to get to them. Any file type, up to 20 MB each.",
  },
];

export const WelcomeScreen = ({
  businessName,
  onContinue,
}: WelcomeScreenProps) => (
  <Stack
    fullWidth
    align="center"
    justify="center"
    className="min-h-[calc(100vh-67px)] bg-surface-secondary px-4 py-10"
  >
    <Stack gap="lg" align="stretch" fullWidth className="max-w-2xl">
      <Typography variant="titleW2">
        Welcome to Volca, let&apos;s get you going!
      </Typography>

      <Card className="w-full border-stroke-tertiary shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <Stack gap="lg" align="stretch" fullWidth>
            <Stack gap="sm" fullWidth>
              <Typography variant="subtitleW2">
                We will walk you through four short steps so we can set up
                payroll for {businessName}.
              </Typography>
              <Typography variant="bodyW2" textColor="secondary">
                On the next pages we will ask you for:
              </Typography>
            </Stack>

            {ONBOARDING_ASKS.map(({ icon: Icon, title, detail }) => (
              <Stack
                key={title}
                direction="row"
                gap="md"
                align="start"
                fullWidth
              >
                <Icon className="mt-0.5 h-7 w-7 shrink-0 text-orange-60" />
                <Stack gap="xs" className="min-w-0 flex-1">
                  <Typography variant="subtitleW3">{title}</Typography>
                  <Typography variant="bodyW2" textColor="secondary">
                    {detail}
                  </Typography>
                </Stack>
              </Stack>
            ))}

            <Separator />

            <Typography variant="captionW1" textColor="tertiary">
              You can stop and come back at any point. Once you have sent
              everything, we build a first version of your payroll from it,
              which usually takes a few days.
            </Typography>

            <Stack direction="row" fullWidth>
              <Button
                size="sm"
                onClick={onContinue}
                rightIcon={<ArrowRightIcon className="h-4 w-4" />}
              >
                Get started
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  </Stack>
);
